
import JigTextField from '@/components/input/JigTextField.vue'
import LabelWithHint from '@/components/input/LabelWithHint.vue'
import PromptYesNo from '@/components/modals/PromptYesNo.vue'
import { SubscriptionConst, TenantConst } from '@/constants'
import { FlexFeatureField, Tenant } from '@/store/modules/app/types'
import { DynaconfConfig, Namespace } from '@/store/types'
import { AppHelper } from '@/utils/app-helper'
import { ValidationRules } from '@/utils/input-validation'
import { TenantHelpers } from '@/utils/tenant-helpers'
import { ValidationRulesType } from '@/utils/type'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Action, State } from 'vuex-class'

@Component({
  components: {
    JigTextField,
    LabelWithHint,
    PromptYesNo,
  },
})
export default class TenantAdminTab extends Vue {
  @Prop({ required: true }) editedTenant!: Tenant
  @Prop({ type: Boolean, default: false }) isSuperUser!: boolean
  @Prop({ required: true }) dynaconfConfig!: DynaconfConfig
  @Prop({ required: true }) pricingSetsValue!: SubscriptionConst.Subscription[]
  @Prop({ required: true }) subscriptionTiersValue!: SubscriptionConst.Subscription[]
  @Prop({ required: true }) subscriptionManagedByTypeValue!: any[]
  @Prop({ required: true }) customFeaturesModels!: Record<string, any>
  @Prop({ required: true }) customFeaturesFlexSwitches!: Record<string, FlexFeatureField>
  @Prop({ required: true }) customFeaturesFlexTextFields!: Record<string, FlexFeatureField>
  @Prop({ required: true }) customFeaturesDefaultValues!: Record<string, any>
  @Prop({ required: true }) crmOtherEditableModels!: Record<string, any>
  @Prop({ required: true }) crmOtherEditableSwitches!: Record<string, FlexFeatureField>
  @Prop({ required: true }) crmOtherEditableTextFields!: Record<string, FlexFeatureField>
  @Prop({ type: Boolean, default: false }) allowMovoJigs!: boolean
  @Prop({ type: Boolean, default: false }) allowJigStats!: boolean
  @Prop({ type: Boolean, default: true }) AVPFeatureGateEnabled!: boolean
  @Prop({ type: Boolean, default: false }) lacksTenantManagePermission!: boolean
  @Prop({ required: true }) noneNegativeNumberRules!: any

  @State('apiError', { namespace: Namespace.Utils })
  public apiError!: string

  @State('myTenant', { namespace: Namespace.App })
  public myTenant!: Tenant

  @Action('syncAuth0SSOConfig', { namespace: Namespace.App })
  public syncAuth0SSOConfig: any
  @Action('deleteTenantById', { namespace: Namespace.App })
  public deleteTenantById: any
  @Action('setMyTenant', { namespace: Namespace.App })
  public setMyTenant: any
  @Action('loadMyTenant', { namespace: Namespace.App })
  public loadMyTenant: any

  public $refs!: Vue['$refs'] & {
    deletePromptYesNo: PromptYesNo
    savePromptYesNo: PromptYesNo
  }

  private formIsValid: boolean = false
  private isSyncingSSO: boolean = false
  private disabledTextField: string = '-1'
  private disabledSwitchField: boolean = false
  private readonly crmStringArrayRules: ValidationRulesType = [
    (value: any) => {
      const normalizedValue = value == null ? '' : String(value).trim()

      if (normalizedValue.length === 0) {
        return true
      }

      const items = normalizedValue.split(',').map((item) => item.trim())
      if (items.some((item) => item.length === 0)) {
        return 'Use comma-separated values (e.g. tag-a, tag-b)'
      }

      return true
    },
  ]

  // Local copies for .sync-like behaviour
  private localAllowMovoJigs: boolean = false
  private localAllowJigStats: boolean = false
  private localAVPFeatureGateEnabled: boolean = true

  @Watch('allowMovoJigs', { immediate: true })
  private onAllowMovoJigsChanged(val: boolean) {
    this.localAllowMovoJigs = val
  }

  @Watch('localAllowMovoJigs')
  private onLocalAllowMovoJigsChanged(val: boolean) {
    this.$emit('update:allowMovoJigs', val)
  }

  @Watch('allowJigStats', { immediate: true })
  private onAllowJigStatsChanged(val: boolean) {
    this.localAllowJigStats = val
  }

  @Watch('localAllowJigStats')
  private onLocalAllowJigStatsChanged(val: boolean) {
    this.$emit('update:allowJigStats', val)
  }

  @Watch('AVPFeatureGateEnabled', { immediate: true })
  private onAVPFeatureGateEnabledChanged(val: boolean) {
    this.localAVPFeatureGateEnabled = val
  }

  @Watch('localAVPFeatureGateEnabled')
  private onLocalAVPFeatureGateEnabledChanged(val: boolean) {
    this.$emit('update:AVPFeatureGateEnabled', val)
  }

  private get additionalSeatsRules(): ValidationRulesType {
    return [
      ...ValidationRules.RequiredNumber,
      (v: any) =>
        this.editedTenant.Subscription.SeatsIncludedInTier + v > 0 || 'The sum of seats included and additional seats should be positive',
    ]
  }

  private get dialogActiveLabel(): string {
    return this.editedTenant.Active ? 'Active' : 'Inactive'
  }

  private get allowMovoJigsLabel(): string {
    return this.switchFieldLabel('Movo Jigs', this.localAllowMovoJigs)
  }

  private get allowJigStatsLabel(): string {
    return this.switchFieldLabel('Jig Stats', this.localAllowJigStats)
  }

  private get AVPFeatureGateLabel(): string {
    return this.switchFieldLabel('Full AVP access', !this.localAVPFeatureGateEnabled)
  }

  private get isPricingSetWithSeatsOrMaxJigs(): boolean {
    const targetPricingSet = this.dynaconfConfig.pricing.sets[this.editedTenant.Subscription.PricingSet]

    return (
      targetPricingSet &&
      this.editedTenant.Subscription.TierType != '' &&
      (targetPricingSet.tiers[this.editedTenant.Subscription.TierType].maxJigs != null ||
        targetPricingSet.tiers[this.editedTenant.Subscription.TierType].seatsIncluded != null)
    )
  }

  private get isMaxJigsSet(): boolean {
    const targetPricingSet = this.dynaconfConfig.pricing.sets[this.editedTenant.Subscription.PricingSet]
    // When MaxJigs === 0 it means unlimited
    return (
      this.isPricingSetWithSeatsOrMaxJigs &&
      targetPricingSet.tiers[this.editedTenant.Subscription.TierType].maxJigs != null &&
      this.editedTenant.Subscription.MaxJigs !== 0
    )
  }

  private get ssoEditableCRMFields(): string[] {
    return TenantConst.CRMSSOEditableFields
  }

  private get readonlyCRMFields(): string[] {
    return Object.keys(this.editedTenant.CRM.Available).filter(
      (key) => !TenantConst.CRMSSOEditableFields.includes(key) && !TenantConst.CRMOtherEditableFields.includes(key)
    )
  }

  private get hasCRMOtherEditableFields(): boolean {
    return Object.keys(this.crmOtherEditableSwitches).length > 0 || Object.keys(this.crmOtherEditableTextFields).length > 0
  }

  private get allowCustombranding(): boolean {
    return this.editedTenant.Subscription.AllowCustomBranding
  }

  private get deleteButtonDisabled(): boolean {
    let disabled = false
    if (!this.allowCustombranding && !this.isSuperUser) {
      disabled = true
    }
    if (!disabled && this.lacksTenantManagePermission) {
      disabled = true
    }
    if (!disabled) {
      // Don't allow deletion of tenants that have existing users
      disabled = this.editedTenant.UserCount === undefined || this.editedTenant.UserCount > 0
    }
    return disabled
  }

  // Disable the team field and active/inactive switcher when it's JigSpaceStaff tenant.
  private get isTenantJigSpaceStaff(): boolean {
    return TenantHelpers.IsTenantJigSpaceStaff(this.editedTenant)
  }

  /**
   * Orders flex switches so that dependent fields always appear directly after
   * the parent field they depend on (via DependsOn). Root fields (no DependsOn)
   * come first, each followed by its dependents; orphaned dependents are appended
   * at the end. Backend currently sends no dependent fields, but the ordering is
   * preserved for when they are re-introduced.
   */
  public get orderedCustomFeaturesFlexSwitches(): Record<string, FlexFeatureField> {
    const switches = this.customFeaturesFlexSwitches
    const result: Record<string, FlexFeatureField> = {}

    const roots = Object.keys(switches).filter((key) => !switches[key].DependsOn)
    const dependents = Object.keys(switches).filter((key) => !!switches[key].DependsOn)

    roots.forEach((rootKey) => {
      result[rootKey] = switches[rootKey]
      dependents.forEach((depKey) => {
        if (switches[depKey].DependsOn === rootKey) {
          result[depKey] = switches[depKey]
        }
      })
    })

    // Any dependents whose parent isn't in switches — append at end
    dependents.forEach((depKey) => {
      if (!result[depKey]) {
        result[depKey] = switches[depKey]
      }
    })

    return result
  }

  private get availableFlexFields() {
    if (this.editedTenant.Subscription.SubscriptionFeatures.Available == null) {
      return {}
    }

    return this.editedTenant.Subscription.SubscriptionFeatures.Available
  }

  private get totalSeats(): number {
    if (this.editedTenant == null || this.editedTenant.Subscription == null) {
      return -1
    }

    const seatsIncluded: number =
      typeof this.editedTenant.Subscription.SeatsIncludedInTier !== 'number' ? 0 : this.editedTenant.Subscription.SeatsIncludedInTier
    const additionalSeats: number =
      typeof this.editedTenant.Subscription.AdditionalSeats !== 'number' ? 0 : this.editedTenant.Subscription.AdditionalSeats

    return seatsIncluded + additionalSeats
  }

  private switchFieldLabel(labelCopy: string, enabled: boolean) {
    return enabled ? `${labelCopy} enabled` : `${labelCopy} disabled`
  }

  private flexFieldLabel(key: string) {
    return SubscriptionConst.CustomFeaturesLabels[key]
  }

  private flexFieldAttrs(field: FlexFeatureField) {
    switch (field.Type) {
      case 'int':
        return {
          rules: this.noneNegativeNumberRules,
        }
      case 'float':
        return {
          rules: this.noneNegativeNumberRules,
        }
      default:
        return {}
    }
  }

  private isFlexSwitchDisabledByDependency(field: FlexFeatureField): boolean {
    if (!field.DependsOn) {
      return false
    }
    return this.customFeaturesModels[field.DependsOn] !== field.EnabledWhen
  }

  private crmFieldAttrs(field: FlexFeatureField) {
    if (field.Type === SubscriptionConst.FlexFeatureFieldType.string_array) {
      return {
        rules: this.crmStringArrayRules,
      }
    }

    return {}
  }

  private onUpdateDataFromTier() {
    this.$emit('update-data-from-tier')
  }

  private async syncAuth0SSO() {
    this.isSyncingSSO = true
    await this.syncAuth0SSOConfig(this.editedTenant.ID)
    await this.loadMyTenant({ TenantId: this.editedTenant.ID })
    this.$emit('saved')
    this.isSyncingSSO = false
  }

  private showDeleteTenantPrompt(t: Tenant) {
    this.$refs.deletePromptYesNo.ShowPrompt()
  }

  private async deleteTenant(t: Tenant) {
    await this.deleteTenantById(t.ID)
    if (this.apiError.length === 0) {
      this.setMyTenant({
        TenantId: TenantHelpers.GetJigSpaceStaffTenantID(),
      })
      this.$router.push('/tenants')
    }
  }

  private showSaveTenantPrompt(t: Tenant) {
    this.$refs.savePromptYesNo.ShowPrompt()
  }

  private onSavePromptSuccess(updatedTenant: Tenant) {
    this.$emit('save', updatedTenant)
  }
}
